/**
 * Escape currency `$` so MathJax does not pair two amounts and typeset the
 * prose between them as maths.
 *
 * "priced over $40. The scallop dish is priced over $40." has two dollars, so
 * MathJax treats them as inline-math delimiters: the sentence between them
 * renders in italic maths and both `$` disappear. The same thing eats the HTML
 * price table in Specimen Problem Solving.
 *
 * MathJax pairs unescaped `$` left to right, so splitting on them puts the
 * content it will typeset at odd indices. A dollar is currency when it is
 * followed by a digit AND the span it opens is not maths but prose or HTML
 * markup. Genuine maths spans such as `$3:1$`, `$b = -4a$` or `$2x + 3 = 7$`
 * open a span with no words and no tags, so they are left alone, and any block
 * holding a LaTeX control sequence, `^` or `_` is skipped outright.
 *
 * When one amount in a block is found, every amount in that block is escaped.
 * Escaping only some of them would shift the pairing and smash a different
 * sentence instead.
 *
 * `\$` is MathJax's own escape and renders as a literal dollar sign.
 */
const LATEX_HINT = /\\[a-zA-Z]+|[\^_]/;
const HTML_TAG = /<[/a-zA-Z]/;
const WORD = /\b[A-Za-z]{3,}\b/g;

function opensProse(segment) {
  if (HTML_TAG.test(segment)) return true;
  return (segment.match(WORD) || []).length >= 3;
}

export function escapeCurrencyDollars(text) {
  if (typeof text !== 'string' || !text.includes('$')) return text || '';
  if (LATEX_HINT.test(text)) return text;

  const positions = [];
  for (let i = 0; i < text.length; i += 1) {
    if (text[i] !== '$') continue;
    if (text[i - 1] === '\\') continue;
    positions.push(i);
  }
  if (positions.length < 2) return text;

  // A field that is entirely one `$...$` span is prose somebody wrapped in
  // dollars, not an amount: ESAT has options like "$6 alpha decays and 6
  // beta-minus decays$". Escaping the opening dollar would leave the closing
  // one visible, so leave the whole shape alone. Unwrapping it is what the
  // disabled LATEX_TEXT_MODIFIERS_ENABLED path used to do.
  const trimmed = text.trim();
  if (positions.length === 2 && trimmed.startsWith('$') && trimmed.endsWith('$')) return text;

  // A dollar followed by a digit is a candidate amount.
  const amounts = positions.filter((i) => /[0-9]/.test(text[i + 1] || ''));
  if (!amounts.length) return text;

  // Two ways a block is currency rather than maths. Either every dollar in it
  // is an amount, so there is no maths for them to delimit: "$2, $5 and $10"
  // would otherwise typeset "2, " and lose both signs. Or an amount opens a
  // span MathJax would typeset as prose or HTML markup, which is the case that
  // smashes a sentence or a price table.
  const allAmounts = amounts.length === positions.length;
  const smashes = amounts.some((i) => {
    const next = positions.find((p) => p > i);
    return opensProse(text.slice(i + 1, next === undefined ? text.length : next));
  });
  if (!allAmounts && !smashes) return text;

  const escapeAt = new Set(amounts);
  let out = '';
  let prev = 0;
  positions.forEach((i) => {
    if (!escapeAt.has(i)) return;
    out += `${text.slice(prev, i)}\\$`;
    prev = i + 1;
  });
  return out + text.slice(prev);
}
